// theme.js
import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#2196F3",
      dark: "#1976D2",
      light: "#64B5F6",
    },
    secondary: {
      main: "#9C27B0",
      dark: "#7B1FA2",
      light: "#BA68C8",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
      // Main gradient background - now light
      gradient: "linear-gradient(135deg, #ffffff, #f5f5f5)",
      // Glass morphism backgrounds - now light
      glass: "rgba(255, 255, 255, 0.9)",
      glassLight: "rgba(255, 255, 255, 0.7)",
      glassStrong: "rgba(255, 255, 255, 0.95)",
      // Header/AppBar - light glass
      header: "rgba(255, 255, 255, 0.9)",
      // Drawer - light
      drawer: "rgba(255, 255, 255, 0.95)",
      // Card backgrounds - white
      card: "#ffffff",
      cardHover: "rgba(33, 150, 243, 0.1)",
      // Button backgrounds
      button: "#475B63",
      buttonGradient: "linear-gradient(45deg, #8fbdcf, #475B63)",
      // Section backgrounds - light
      section: "rgba(245, 245, 245, 0.5)",
      sectionLight: "rgba(255, 255, 255, 0.8)",
      // Overlay backgrounds
      overlay: "rgba(255, 255, 255, 0.95)",
      // Legacy colors (keeping for backward compatibility)
      white: "#ffffff",
      appbar: "#475B63",
      dialog: "#d19191",
      notification: "#EAEEF0",
      input: "#FFFFFF",
      dataGrid: "#FFFFFF",
      l1: "#EAEEF0",
      l2: "#e1c9a8",
      l3: "#d19191",
      l4: "#475B63",
      l0: "#FFFFFF",
      m2: "#ffad61",
      p1: "#EAEEF0",
      p2: "#dae7ed",
      p3: "#afd2e3",
    },
    text: {
      primary: "#333333",
      secondary: "#555555",
      tertiary: "#666666",
      quaternary: "#777777",
      disabled: "#999999",
      // Dark text variants
      dark: "#333333",
      darkSecondary: "#666666",
      // Legacy colors (keeping for backward compatibility)
      white: "#ffffff",
      black: "#000000",
      validInput: "#475B63",
      invalidInput: "#d19191",
      disabledInput: "#a0a0a0",
      light: "#EAEEF0",
      l1: "#000000",
      l2: "#000000",
    },
    // Custom color palette for specific elements
    brand: {
      gradient: "linear-gradient(45deg, #8fbdcf, #475B63)",
      gradientHover: "linear-gradient(45deg, #8fbdcf, #475B63)",
      gradientText: "linear-gradient(45deg, #8fbdcf, #eda747)",
    },
    // Status colors
    status: {
      available: "#4CAF50",
      availableBackground: "rgba(76, 175, 80, 0.1)",
      beta: "#FF9800",
      betaBackground: "rgba(255, 152, 0, 0.1)",
      comingSoon: "#9E9E9E",
      comingSoonBackground: "rgba(158, 158, 158, 0.1)",
    },
    // Border colors - now darker for visibility on white
    border: {
      light: "rgba(0, 0, 0, 0.1)",
      medium: "rgba(0, 0, 0, 0.2)",
      strong: "rgba(0, 0, 0, 0.3)",
      primary: "rgba(33, 150, 243, 0.3)",
      secondary: "rgba(156, 39, 176, 0.3)",
    },
    // Chip colors - adjusted for light theme
    chip: {
      primary: {
        background: "rgba(33, 150, 243, 0.1)",
        color: "#1976D2",
        border: "rgba(33, 150, 243, 0.3)",
      },
      secondary: {
        background: "rgba(156, 39, 176, 0.1)",
        color: "#7B1FA2",
        border: "rgba(156, 39, 176, 0.3)",
      },
    },
    light: {
      l100: "#f0f0f0",
    },
  },
  typography: {
    fontFamily: "Arial, sans-serif",
    h1: {
      fontWeight: "bold",
      color: "#333333",
    },
    h2: {
      fontWeight: "bold",
      color: "#333333",
    },
    h3: {
      fontWeight: "bold",
      color: "#333333",
    },
    h4: {
      fontWeight: "bold",
      color: "#333333",
    },
    h5: {
      fontWeight: "bold",
      color: "#333333",
    },
    h6: {
      fontWeight: "bold",
      color: "#333333",
    },
  },
  // Custom shadows
  shadows: [
    "none",
    "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
    "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
    "0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)",
    "0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
    "0 4px 16px rgba(33, 150, 243, 0.3)",
    "0 6px 20px rgba(33, 150, 243, 0.4)",
    "0 8px 32px rgba(0, 0, 0, 0.2)",
    "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
    "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
    "0 20px 40px rgba(0,0,0,0.25), 0 15px 15px rgba(0,0,0,0.22)",
    "0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)",
    "0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
    "0 24px 48px rgba(0,0,0,0.35), 0 18px 18px rgba(0,0,0,0.22)",
    "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
    "0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)",
    "0 10px 20px rgba(0,0,0,0.19), 0 6px 6px rgba(0,0,0,0.23)",
    "0 14px 28px rgba(0,0,0,0.25), 0 10px 10px rgba(0,0,0,0.22)",
    "0 19px 38px rgba(0,0,0,0.30), 0 15px 12px rgba(0,0,0,0.22)",
    "0 24px 48px rgba(0,0,0,0.35), 0 18px 18px rgba(0,0,0,0.22)",
    "0 29px 58px rgba(0,0,0,0.40), 0 22px 22px rgba(0,0,0,0.22)",
    "0 34px 68px rgba(0,0,0,0.45), 0 26px 26px rgba(0,0,0,0.22)",
    "0 39px 78px rgba(0,0,0,0.50), 0 30px 30px rgba(0,0,0,0.22)",
  ],
  // Custom transitions
  transitions: {
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195,
    },
    easing: {
      easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      easeOut: "cubic-bezier(0.0, 0, 0.2, 1)",
      easeIn: "cubic-bezier(0.4, 0, 1, 1)",
      sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    },
  },
});

export default theme;
